<template>
  <section class="parameter-card" :class="{ 'has-range': numericRanges.length }" :aria-label="title + '筛选'">
    <header>
      <h3 :title="title">{{ title }}</h3>
      <button v-if="hasSelection" type="button" :disabled="disabled" @click="clear">清除</button>
    </header>
    <input v-model="keyword" class="option-search" type="search" :placeholder="'搜索' + title" :aria-label="'搜索' + title + '选项'">
    <form v-if="numericRanges.length" class="range-form" @submit.prevent="applyRange">
      <div class="range-fields">
        <input v-model="min" type="text" inputmode="decimal" :placeholder="currentRange ? currentRange.min : '最小值'" :aria-label="title + '最小值'" :disabled="disabled" @input="rangeError = ''">
        <span>—</span>
        <input v-model="max" type="text" inputmode="decimal" :placeholder="currentRange ? currentRange.max : '最大值'" :aria-label="title + '最大值'" :disabled="disabled" @input="rangeError = ''">
        <select v-if="numericRanges.length > 1" v-model="unit" :aria-label="title + '单位'" :disabled="disabled" @change="changeUnit">
          <option v-for="item in numericRanges" :key="item.unit" :value="item.unit">{{ item.unit || '无单位' }}</option>
        </select>
        <span v-else-if="unit" class="range-unit">{{ unit }}</span>
      </div>
      <div class="range-actions"><span v-if="rangeError" role="alert">{{ rangeError }}</span><button type="submit" :disabled="disabled">应用范围</button></div>
    </form>
    <div ref="optionList" class="option-list" @scroll.passive="scrollTop = $event.target.scrollTop">
      <div v-if="virtualized" :style="{ height: startIndex * 31 + 'px' }" aria-hidden="true"></div>
      <label v-for="value in renderedOptions" :key="value" class="check-option" :class="{ selected: selected.includes(value), 'virtual-option': virtualized }">
        <input type="checkbox" :checked="selected.includes(value)" :disabled="disabled" @change="toggle(value, $event.target.checked)">
        <span :title="optionLabel(value)">{{ optionLabel(value) }}</span>
      </label>
      <div v-if="virtualized" :style="{ height: (visibleOptions.length - startIndex - renderedOptions.length) * 31 + 'px' }" aria-hidden="true"></div>
      <p v-if="!visibleOptions.length" class="no-options">{{ keyword ? '无匹配选项' : '暂无可选项' }}</p>
    </div>
  </section>
</template>

<script>
  import { uniqueValues, validateRange, displayParamValue } from './product-list-filters'

  export default {
    name: 'ProductFilterCard',
    props: {
      title: { type: String, required: true },
      options: { type: Array, default: () => [] },
      selected: { type: Array, default: () => [] },
      valueUnit: { type: String, default: '' },
      numericRanges: { type: Array, default: () => [] },
      range: { type: Object, default: () => ({}) },
      disabled: Boolean
    },
    data() { return { keyword: '', min: '', max: '', unit: '', rangeError: '', scrollTop: 0 } },
    computed: {
      visibleOptions() {
        const keyword = this.keyword.trim().toLowerCase()
        return uniqueValues(this.options.concat(this.selected)).filter(value => displayParamValue(value, this.valueUnit).toLowerCase().includes(keyword))
      },
      virtualized() { return this.visibleOptions.length > 200 },
      startIndex() { return this.virtualized ? Math.max(0, Math.min(Math.floor(this.scrollTop / 31) - 5, this.visibleOptions.length - 22)) : 0 },
      renderedOptions() { return this.virtualized ? this.visibleOptions.slice(this.startIndex, this.startIndex + 22) : this.visibleOptions },
      hasSelection() { return this.selected.length || this.range.min !== undefined || this.range.max !== undefined },
      currentRange() { return this.numericRanges.find(item => item.unit === this.unit) }
    },
    watch: {
      keyword: 'resetScroll',
      options: 'resetScroll',
      range: { handler: 'syncRange', immediate: true, deep: true },
      numericRanges: { handler: 'syncRange', deep: true }
    },
    methods: {
      resetScroll() {
        this.scrollTop = 0
        this.$nextTick(() => { if (this.$refs.optionList) this.$refs.optionList.scrollTop = 0 })
      },
      optionLabel(value) { return displayParamValue(value, this.valueUnit) },
      syncRange() {
        this.min = this.range.min == null ? '' : String(this.range.min)
        this.max = this.range.max == null ? '' : String(this.range.max)
        this.unit = this.range.unit == null ? ((this.numericRanges[0] || {}).unit || '') : this.range.unit
        this.rangeError = ''
      },
      changeUnit() { this.min = ''; this.max = ''; this.rangeError = '' },
      toggle(value, checked) {
        const values = checked ? uniqueValues(this.selected.concat(value)) : this.selected.filter(item => item !== value)
        this.$emit('change', { values })
      },
      clear() { this.min = ''; this.max = ''; this.rangeError = ''; this.$emit('change', { values: [] }) },
      applyRange() {
        this.rangeError = validateRange(this.min, this.max)
        if (this.rangeError) return
        this.$emit('change', { min: this.min.trim(), max: this.max.trim(), unit: this.unit })
      }
    }
  }
</script>

<style scoped>
  .parameter-card { flex: 0 0 218px; min-width: 0; height: 348px; padding: 15px; display: flex; flex-direction: column; border: 1px solid #dfe4ec; border-radius: 10px; background: #fff; box-sizing: border-box; }
  .parameter-card.has-range { flex-basis: 286px; }
  header { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 12px; }
  h3 { min-width: 0; margin: 0; color: #111827; font-size: 15px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  button { padding: 0; border: 0; background: transparent; color: #1684ff; cursor: pointer; white-space: nowrap; font-size: 12px; }
  input, select { min-width: 0; border: 1px solid #d5d9e2; border-radius: 5px; background: #fff; color: #344054; font: inherit; }
  input:focus, select:focus, button:focus-visible { outline: 2px solid #1684ff; outline-offset: 1px; }
  .option-search { width: 100%; height: 32px; padding: 0 9px; margin-bottom: 12px; flex-shrink: 0; box-sizing: border-box; font-size: 12px; }
  .range-fields { display: flex; align-items: center; gap: 5px; }
  .range-fields input { width: 0; flex: 1; height: 30px; padding: 0 6px; font-size: 12px; box-sizing: border-box; }
  .range-fields select { max-width: 68px; height: 30px; font-size: 12px; }
  .range-unit { max-width: 64px; overflow-wrap: anywhere; font-size: 12px; }
  .range-actions { min-height: 29px; display: flex; align-items: center; justify-content: flex-end; gap: 5px; }
  .range-actions span { flex: 1; color: #e34848; font-size: 11px; }
  .option-list { min-height: 0; flex: 1; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #b8c4d6 transparent; }
  .option-list::-webkit-scrollbar { width: 5px; }
  .option-list::-webkit-scrollbar-thumb { background: #b8c4d6; border-radius: 4px; }
  .check-option { min-height: 31px; display: flex; align-items: flex-start; gap: 8px; padding: 5px 2px; color: #344054; cursor: pointer; font-size: 13px; line-height: 21px; }
  .check-option input { width: 14px; height: 14px; flex: 0 0 14px; margin: 3px 0 0; accent-color: #1684ff; cursor: pointer; }
  .check-option span { overflow-wrap: anywhere; }
  .check-option.virtual-option { height: 31px; min-height: 31px; box-sizing: border-box; }
  .virtual-option span { min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .check-option:hover, .check-option.selected { color: #1684ff; }
  .no-options { color: #9ca3af; font-size: 12px; }
  button:disabled, input:disabled, select:disabled { cursor: not-allowed; opacity: .6; }
</style>
